"use client"

import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Transition,
} from "@headlessui/react"
import { Fragment, useEffect, useMemo, useState } from "react"
import ReactCountryFlag from "react-country-flag"

import { useParams, usePathname, useRouter } from "next/navigation"
import { HttpTypes } from "@medusajs/types"

import { updateRegionWithValidation } from "@/lib/data/cart"
import { toast } from "@/lib/helpers/toast"
import {
  getCurrencySymbol,
  detectCountryFromGPS,
  detectCountryFromIPClient,
} from "@/lib/helpers/geolocation"
import {
  getSessionLocalePreference,
  setSessionLocalePreference,
} from "@/lib/helpers/currency-session"

type CountryOption = {
  country: string
  region: string
  label: string
  currency: string
  symbol: string
}

type CountrySelectProps = {
  regions: HttpTypes.StoreRegion[]
}

const CountrySelect = ({ regions }: CountrySelectProps) => {
  const { locale: countryCode } = useParams()
  const router = useRouter()
  const pathname = usePathname()
  const currentPath = pathname.startsWith(`/${countryCode}`)
    ? pathname.slice(`/${countryCode}`.length) || ""
    : pathname

  const options = useMemo(() => {
    return regions
      ?.map((r) => {
        const currency = (r.currency_code || "usd").toUpperCase()
        const symbol = getCurrencySymbol(r.currency_code || "usd")
        return r.countries?.map((c) => ({
          country: c.iso_2?.toLowerCase() || "",
          region: r.id,
          label: c.display_name || c.iso_2?.toUpperCase() || "",
          currency,
          symbol,
        }))
      })
      .flat()
      .filter((o): o is CountryOption => Boolean(o && o.country))
      .sort((a, b) => (a?.label ?? "").localeCompare(b?.label ?? ""))
  }, [regions])

  const [current, setCurrent] = useState<CountryOption | undefined>(() => {
    if (countryCode && options?.length) {
      return options.find(
        (o) => o.country === (countryCode as string).toLowerCase()
      )
    }
    return undefined
  })
  const [isGpsDetecting, setIsGpsDetecting] = useState(false)

  useEffect(() => {
    if (countryCode && options?.length) {
      const option = options.find(
        (o) => o.country === (countryCode as string).toLowerCase()
      )
      if (option) {
        setCurrent(option)
      }
    }
  }, [options, countryCode])

  // Silent IP auto-detection on first visit if no session preference exists yet
  useEffect(() => {
    if (typeof window === "undefined") return
    // Never auto-redirect if customer is currently in checkout
    if (currentPath.includes("checkout")) return
    if (sessionStorage.getItem("geo_ip_checked")) return
    sessionStorage.setItem("geo_ip_checked", "true")

    const existingSession = getSessionLocalePreference()
    if (!existingSession && options?.length) {
      detectCountryFromIPClient().then((detectedCountry) => {
        if (
          detectedCountry &&
          detectedCountry !== (countryCode as string)?.toLowerCase()
        ) {
          const match = options.find(
            (o) => o.country === detectedCountry.toLowerCase()
          )
          if (match) {
            setSessionLocalePreference(match.country)
            updateRegionWithValidation(match.country, currentPath)
              .then((result) => {
                router.push(result.newPath)
                router.refresh()
              })
              .catch(() => {})
          }
        }
      }).catch(() => {})
    }
  }, [options, countryCode, currentPath, router])

  const handleGpsDetect = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (isGpsDetecting) return
    setIsGpsDetecting(true)

    try {
      const gpsCountry = await detectCountryFromGPS()
      if (!gpsCountry) {
        toast.error({
          title: "GPS Location Unavailable",
          description:
            "Could not determine location or permission was denied. Try choosing manually.",
        })
        return
      }

      const match = options?.find(
        (o) => o.country === gpsCountry.toLowerCase()
      )

      if (match) {
        setSessionLocalePreference(match.country)
        toast.info({
          title: "Location Detected",
          description: `Switched to ${match.label} (${match.currency} ${match.symbol}).`,
        })
        const result = await updateRegionWithValidation(
          match.country,
          currentPath
        )
        router.push(result.newPath)
        router.refresh()
      } else {
        toast.info({
          title: "Region Not Directly Supported",
          description: `Your detected country (${gpsCountry.toUpperCase()}) is not in an active region. Defaulting to USD.`,
        })
      }
    } catch (err: any) {
      toast.error({
        title: "GPS Error",
        description: err?.message || "Failed to detect location.",
      })
    } finally {
      setIsGpsDetecting(false)
    }
  }

  const handleChange = async (option: CountryOption) => {
    try {
      // 1. Store the user's manual selection in session cookie so it stays for the browsing session
      setSessionLocalePreference(option.country)

      // 2. Update the region & handle removed items if any
      const result = await updateRegionWithValidation(
        option.country,
        currentPath
      )

      if (result.removedItems.length > 0) {
        const itemsList = result.removedItems.join(", ")
        toast.info({
          title: "Cart updated",
          description: `${itemsList} ${
            result.removedItems.length === 1 ? "is" : "are"
          } not available in ${option.label} and ${
            result.removedItems.length === 1 ? "was" : "were"
          } removed from your cart.`,
        })
      }

      // 3. Navigate to new locale path
      router.push(result.newPath)
      router.refresh()
    } catch (error: any) {
      toast.error({
        title: "Error switching region",
        description:
          error?.message || "Failed to update region. Please try again.",
      })
    }
  }

  return (
    <div className="flex items-center relative" data-testid="country-currency-selector">
      <Listbox
        onChange={handleChange}
        value={current}
        by="country"
      >
        <ListboxButton className="relative flex items-center justify-between gap-1.5 px-2.5 h-9 bg-component-secondary hover:bg-gray-100 dark:hover:bg-gray-800 text-left cursor-pointer border rounded-md text-xs font-medium transition-colors shadow-2xs">
          {current ? (
            <span className="flex items-center gap-1.5">
              {/* @ts-ignore */}
              <ReactCountryFlag
                alt={`${current.country?.toUpperCase()} flag`}
                svg
                style={{
                  width: "16px",
                  height: "16px",
                  borderRadius: "2px",
                }}
                countryCode={current.country ?? ""}
              />
              <span className="font-semibold text-gray-800 dark:text-gray-200">
                {current.currency}
              </span>
              <span className="text-gray-500 text-[11px] font-normal">
                ({current.symbol})
              </span>
            </span>
          ) : (
            <span className="text-xs text-gray-500">Currency</span>
          )}
          <svg
            className="h-3 w-3 text-gray-400 ml-0.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </ListboxButton>

        <Transition
          as={Fragment}
          leave="transition ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <ListboxOptions className="no-scrollbar absolute right-0 top-full mt-1.5 z-50 w-64 overflow-auto text-xs bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow-xl max-h-72 focus:outline-none p-1">
            {/* GPS Location Button */}
            <button
              type="button"
              onClick={handleGpsDetect}
              disabled={isGpsDetecting}
              className="w-full flex items-center gap-2 px-2.5 py-2 text-xs font-medium text-primary hover:bg-primary/10 rounded-md transition-colors text-left border-b border-gray-100 dark:border-gray-800 mb-1 cursor-pointer disabled:opacity-50"
              data-testid="gps-detect-button"
            >
              {isGpsDetecting ? (
                <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              ) : (
                <svg
                  className="h-3.5 w-3.5 text-primary flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              )}
              <span className="truncate">
                {isGpsDetecting
                  ? "Detecting GPS location..."
                  : "Detect my location (GPS)"}
              </span>
            </button>

            {/* List of Countries and Currencies */}
            {options?.map((o) => (
              <ListboxOption
                key={`${o.country}-${o.region}`}
                value={o}
                className={({ active, selected }) =>
                  `cursor-pointer select-none relative flex items-center justify-between px-2.5 py-1.5 rounded-md transition-colors ${
                    active || selected
                      ? "bg-primary/10 text-primary font-semibold"
                      : "text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800/60"
                  }`
                }
              >
                <span className="flex items-center gap-2 truncate pr-2">
                  {/* @ts-ignore */}
                  <ReactCountryFlag
                    svg
                    style={{
                      width: "16px",
                      height: "16px",
                      borderRadius: "2px",
                    }}
                    countryCode={o.country}
                  />
                  <span className="truncate">{o.label}</span>
                </span>
                <span className="text-[11px] font-mono text-gray-500 dark:text-gray-400 flex-shrink-0">
                  {o.currency} ({o.symbol})
                </span>
              </ListboxOption>
            ))}
          </ListboxOptions>
        </Transition>
      </Listbox>
    </div>
  )
}

export default CountrySelect
